import type { Database } from "@pex/database/types";
import type { SupabaseClient } from "@supabase/supabase-js";

export interface BillingAccount {
  email: string;
  first_name: string | null;
  id: string;
  last_name: string | null;
  paystack_customer_code: string | null;
}

function sharedSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("shared");
}

export async function getAccountByEmail(
  client: SupabaseClient<Database>,
  email: string
): Promise<BillingAccount | null> {
  const { data, error } = await sharedSchema(client)
    .from("accounts")
    .select("id, email, first_name, last_name, paystack_customer_code")
    .eq("email", email)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch account by email: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  return {
    id: data.id as string,
    email: data.email as string,
    first_name: (data.first_name as string | null) ?? null,
    last_name: (data.last_name as string | null) ?? null,
    paystack_customer_code:
      (data.paystack_customer_code as string | null) ?? null,
  };
}

export async function getAccountById(
  client: SupabaseClient<Database>,
  accountId: string
): Promise<BillingAccount | null> {
  const { data, error } = await sharedSchema(client)
    .from("accounts")
    .select("id, email, first_name, last_name, paystack_customer_code")
    .eq("id", accountId)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch account by ID: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  return {
    id: data.id as string,
    email: data.email as string,
    first_name: (data.first_name as string | null) ?? null,
    last_name: (data.last_name as string | null) ?? null,
    paystack_customer_code:
      (data.paystack_customer_code as string | null) ?? null,
  };
}

/**
 * Sets accounts.paystack_customer_code if it is currently null.
 *
 * Called by the webhook handler on subscription.create to cache the Paystack
 * customer code at the account level. Uses a conditional update (only sets if
 * null) to avoid overwriting an already-set code.
 */
export async function setPaystackCustomerCode(
  client: SupabaseClient<Database>,
  accountId: string,
  customerCode: string
): Promise<void> {
  const { error } = await sharedSchema(client)
    .from("accounts")
    .update({
      paystack_customer_code: customerCode,
      updated_at: new Date().toISOString(),
    })
    .eq("id", accountId)
    .is("paystack_customer_code", null);

  if (error) {
    throw new Error(`Failed to set Paystack customer code: ${error.message}`);
  }
}
